import { z } from 'zod';

export type AuthErrorCode =
  | 'invalid-credentials'
  | 'lockout-imminent'
  | 'locked'
  | 'validation-failed'
  | 'network-error'
  | 'unknown';

export interface AuthError {
  code: AuthErrorCode;
  /** Raw message from Rails, when present. Never branch on this — branch on `code`. */
  message: string;
  /** Per-field validation messages, when Rails returns them (registration only). */
  fieldErrors?: Record<string, string[]>;
}

export type AuthResult<T> = { success: true; data: T } | { success: false; error: AuthError };

interface LoginErrorPattern {
  code: AuthErrorCode;
  matches: (message: string) => boolean;
}

/**
 * Classifies the /login error string Rails returns today. A lookup table
 * rather than inline conditionals so a new lockout-copy variant is a new row
 * here, not a new branch at every call site. Mirrors the substrings
 * AccountFormCinderblocks/AccountForm.tsx currently checks inline.
 */
const LOGIN_ERROR_PATTERNS: LoginErrorPattern[] = [
  {
    code: 'lockout-imminent',
    matches: (message) =>
      message.includes('You have one more attempt before your account is locked.'),
  },
  {
    code: 'locked',
    matches: (message) => message.includes('Your account is locked.'),
  },
];

function classifyLoginError(message: string): AuthError {
  const pattern = LOGIN_ERROR_PATTERNS.find((candidate) => candidate.matches(message));
  return { code: pattern?.code ?? 'invalid-credentials', message };
}

const LOCALHOST_HOSTNAME = 'localhost';

function csrfCookieName(): string {
  return window.location.hostname === LOCALHOST_HOSTNAME ? 'XSRF-TOKEN' : 'XSRF-G-TOKEN';
}

function readCookie(name: string): string | undefined {
  const prefix = `${name}=`;
  const entry = document.cookie.split('; ').find((candidate) => candidate.startsWith(prefix));
  if (!entry) return undefined;
  try {
    return decodeURIComponent(entry.slice(prefix.length));
  } catch {
    // A malformed cookie value (e.g. a lone '%') must not surface as a
    // misleading network-error from the caller's unrelated fetch try/catch.
    return undefined;
  }
}

function postJson(
  authBaseUrl: string,
  path: string,
  body: unknown,
  fetchFn: typeof fetch,
): Promise<Response> {
  return fetchFn(`${authBaseUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      'X-CSRF-Token': readCookie(csrfCookieName()) ?? '',
    },
    body: JSON.stringify(body),
  });
}

function getJson(authBaseUrl: string, path: string, fetchFn: typeof fetch): Promise<Response> {
  return fetchFn(`${authBaseUrl}${path}`, {
    method: 'GET',
    credentials: 'include',
    headers: { Accept: 'application/json' },
  });
}

/**
 * Reads and parses a response body against `schema`, never throwing —
 * `AuthResult` is the module's whole contract, so an unparsable or
 * shape-mismatched body must degrade to `undefined`, not reject the promise.
 */
async function parseBody<T extends z.ZodType>(
  response: Response,
  schema: T,
): Promise<z.infer<T> | undefined> {
  const raw: unknown = await response.json().catch(() => undefined);
  const result = schema.safeParse(raw);
  return result.success ? result.data : undefined;
}

// ---------------------------------------------------------------------------
// logIn — POST /login
// ---------------------------------------------------------------------------

const logInResponseSchema = z.object({
  // .nullish(), not .optional(): Rails serializes an absent field as JSON
  // `null` (e.g. `{ error: "...", redirect_to: null }`), which .optional()
  // (undefined-only) rejects.
  redirect_to: z.string().nullish(),
  error: z.string().nullish(),
});

export interface LogInResponse {
  redirectTo?: string;
}

export type LogInCredentials = { email: string; password: string } | { googleIdToken: string };

export async function logIn(
  authBaseUrl: string,
  credentials: LogInCredentials,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<LogInResponse>> {
  const body =
    'googleIdToken' in credentials
      ? { google_id_token: credentials.googleIdToken }
      : { human: { email: credentials.email, password: credentials.password } };

  let response: Response;
  try {
    response = await postJson(authBaseUrl, '/login', body, fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  const data = await parseBody(response, logInResponseSchema);
  if (response.ok) {
    return { success: true, data: { redirectTo: data?.redirect_to ?? undefined } };
  }
  return { success: false, error: classifyLoginError(data?.error ?? '') };
}

// ---------------------------------------------------------------------------
// register — POST /register
// ---------------------------------------------------------------------------

const registerResponseSchema = z.object({
  redirect_to: z.string().nullish(),
  error: z.string().nullish(),
  // Two real shapes: Devise's default validation failure renders a field→messages
  // object; the handle_invalid_phone_number rescue (registrations_controller.rb)
  // renders a flat message array instead.
  errors: z.union([z.record(z.string(), z.array(z.string())), z.array(z.string())]).nullish(),
});

export interface RegisterResponse {
  redirectTo?: string;
}

export type RegisterParams = {
  email: string;
  fullName: string;
  primaryPhone: string;
  redirectUrl?: string;
} & (
  | { password: string }
  | { googleIdToken: string }
  // Passwordless: Rails auto-generates a server-side password and (behind the
  // register_send_password_setup flag) mails a setup link, so no credential
  // rides on the request. See Humans::RegistrationsController#create.
  | { hasGeneratedPassword: true }
);

export async function register(
  authBaseUrl: string,
  params: RegisterParams,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<RegisterResponse>> {
  const body = {
    email: params.email,
    password: 'password' in params ? params.password : undefined,
    google_id_token: 'googleIdToken' in params ? params.googleIdToken : undefined,
    has_generated_password: 'hasGeneratedPassword' in params ? true : undefined,
    full_name: params.fullName,
    primary_phone: params.primaryPhone,
    redirect_url: params.redirectUrl,
  };

  let response: Response;
  try {
    response = await postJson(authBaseUrl, '/register', body, fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  const data = await parseBody(response, registerResponseSchema);
  if (response.ok) {
    return { success: true, data: { redirectTo: data?.redirect_to ?? undefined } };
  }
  if (data?.errors) {
    const fieldErrors = Array.isArray(data.errors) ? { base: data.errors } : data.errors;
    const message = Array.isArray(data.errors) ? (data.errors[0] ?? '') : (data.error ?? '');
    return { success: false, error: { code: 'validation-failed', message, fieldErrors } };
  }
  return { success: false, error: { code: 'unknown', message: data?.error ?? '' } };
}

// ---------------------------------------------------------------------------
// loginWithPasswordlessToken — POST /register with a one-time passwordless token
// ---------------------------------------------------------------------------

const passwordlessTokenLoginResponseSchema = z.object({
  redirect_to: z.string().nullish(),
  error: z.string().nullish(),
});

export interface PasswordlessTokenLoginParams {
  /** The one-time `access_token` from a login-lite / mobile-handoff link. */
  accessToken: string;
  redirectUrl?: string;
}

export interface PasswordlessTokenLoginResponse {
  redirectTo?: string;
}

/**
 * Logs a returning human in by redeeming a one-time passwordless token. Despite
 * the `login` name it POSTs `/register` with `passwordless_token` — Rails routes
 * that param to the PasswordlessAuthenticatable strategy
 * (`Humans::RegistrationsController#create`), the same endpoint and body shaping
 * the legacy session saga used, so the wire contract is unchanged. The response
 * carries the server's post-login destination in `redirect_to`; the login-lite
 * pages compute their own redirect from the URL, so this is surfaced for parity
 * rather than consumed.
 */
export async function loginWithPasswordlessToken(
  authBaseUrl: string,
  params: PasswordlessTokenLoginParams,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<PasswordlessTokenLoginResponse>> {
  const body = {
    passwordless_token: params.accessToken,
    redirect_url: params.redirectUrl,
  };

  // Failure taxonomy is deliberately coarse: a thrown fetch is a `network-error`,
  // and a non-ok response is typed from its HTTP status. Rails renders every
  // passwordless-token failure as a bare 401 with an empty JSON body — unlike the
  // native-strategy login failures, which carry a stable `error` code — so a
  // rejected token (expired, already used, unknown, or not matching the current
  // session) is indistinguishable on the wire. A 401 therefore maps to
  // `invalid-credentials` (the token was rejected) and any other non-ok status to
  // `unknown`. `message` passes through if the server ever supplies one; finer
  // codes need the server to emit them on this branch.
  let response: Response;
  try {
    response = await postJson(authBaseUrl, '/register', body, fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  const data = await parseBody(response, passwordlessTokenLoginResponseSchema);
  if (response.ok) {
    return { success: true, data: { redirectTo: data?.redirect_to ?? undefined } };
  }

  const code: AuthErrorCode = response.status === 401 ? 'invalid-credentials' : 'unknown';
  return { success: false, error: { code, message: data?.error ?? '' } };
}

// ---------------------------------------------------------------------------
// requestHumanStatus — GET /api/v1/humans/status
// ---------------------------------------------------------------------------

// A JSON `null` body is Rails' actual "no account" signal, handled as its own
// case below — not part of this schema, so a malformed non-null body (a
// parse failure) isn't silently conflated with "no account".
const humanStatusResponseSchema = z.object({ has_generated_password: z.boolean().nullish() });

export interface HumanStatusResponse {
  isNewHuman: boolean;
  hasGeneratedPassword: boolean;
}

export async function requestHumanStatus(
  authBaseUrl: string,
  email: string,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<HumanStatusResponse>> {
  let response: Response;
  try {
    response = await getJson(
      authBaseUrl,
      `/api/v1/humans/status?email=${encodeURIComponent(email)}`,
      fetchFn,
    );
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  if (!response.ok) {
    return { success: false, error: { code: 'unknown', message: '' } };
  }

  const raw: unknown = await response.json().catch(() => undefined);
  if (raw === null) {
    return { success: true, data: { isNewHuman: true, hasGeneratedPassword: false } };
  }

  const parsed = humanStatusResponseSchema.safeParse(raw);
  if (!parsed.success) {
    return { success: false, error: { code: 'unknown', message: '' } };
  }
  return {
    success: true,
    data: { isNewHuman: false, hasGeneratedPassword: Boolean(parsed.data.has_generated_password) },
  };
}

// ---------------------------------------------------------------------------
// requestResetPassword — POST /api/v1/humans/reset-password
// ---------------------------------------------------------------------------

const resetPasswordResponseSchema = z.object({ error: z.string().nullish() });

export async function requestResetPassword(
  authBaseUrl: string,
  email: string,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<undefined>> {
  let response: Response;
  try {
    response = await postJson(authBaseUrl, '/api/v1/humans/reset-password', { email }, fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  const data = await parseBody(response, resetPasswordResponseSchema);
  if (response.ok) {
    return { success: true, data: undefined };
  }
  return { success: false, error: { code: 'unknown', message: data?.error ?? '' } };
}

// ---------------------------------------------------------------------------
// requestPasswordReset — GET /humans/status, then POST reset for existing accounts
// ---------------------------------------------------------------------------

/**
 * The human-status-guarded reset flow the UI drives: look the email up first,
 * and only ask Rails to send a reset/setup email when an account exists. A new
 * email resolves successfully with `isNewHuman: true` and no POST, so the
 * caller routes to registration rather than claiming a reset email was sent.
 * Mirrors the legacy saga's `requestPasswordReset` + `maybeResetPassword`
 * branching (`shouldResetPassword = !isNewHuman`).
 */
export async function requestPasswordReset(
  authBaseUrl: string,
  email: string,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<HumanStatusResponse>> {
  const statusResult = await requestHumanStatus(authBaseUrl, email, fetchFn);
  if (!statusResult.success || statusResult.data.isNewHuman) {
    return statusResult;
  }

  const resetResult = await requestResetPassword(authBaseUrl, email, fetchFn);
  if (!resetResult.success) {
    return resetResult;
  }
  return statusResult;
}

// ---------------------------------------------------------------------------
// logOut — GET /session/sign-out
// ---------------------------------------------------------------------------

export async function logOut(
  authBaseUrl: string,
  fetchFn: typeof fetch = fetch,
): Promise<AuthResult<undefined>> {
  let response: Response;
  try {
    response = await getJson(authBaseUrl, '/session/sign-out', fetchFn);
  } catch {
    return { success: false, error: { code: 'network-error', message: '' } };
  }

  if (!response.ok) {
    return { success: false, error: { code: 'unknown', message: '' } };
  }
  return { success: true, data: undefined };
}
